import type { AnyShippingMethod, CheckoutV2Configuration } from "@jti/commerce";

import type { FormFieldFactory } from "@checkout-v2/formFields/types/FormFieldFactory.types";
import { DEFAULT_AT_HOME_SHIPPING_METHOD } from "@checkout-v2/shared";

import type {
  ShippingMethodCodeFieldConfig,
  ShippingMethodCodeFieldOption,
  ShippingMethodDefaultValueOptions,
} from "./shippingMethodCodeFieldFactory.types";

type ShippingMethodFieldFactory = {
  getEligibleValue: (
    shippingMethods: CheckoutV2Configuration["shippingMethods"] | undefined,
    selectedMethodCode: string | undefined,
    options: ShippingMethodDefaultValueOptions
  ) => string;
  getSelectedShippingMethodType: (
    methodCode: string,
    checkoutConfiguration: CheckoutV2Configuration
  ) => AnyShippingMethod["type"] | undefined;
} & FormFieldFactory<
  "shippingMethodCode",
  string,
  [CheckoutV2Configuration["shippingMethods"] | undefined],
  [CheckoutV2Configuration["shippingMethods"] | undefined, ShippingMethodDefaultValueOptions?],
  ShippingMethodCodeFieldConfig
>;

const generateFieldOptions = (
  shippingMethods: CheckoutV2Configuration["shippingMethods"]
): ShippingMethodCodeFieldOption[] => {
  if (!shippingMethods) {
    return [];
  }

  return shippingMethods.map((method) => ({
    value: method.methodCode,
    label: method.label,
    type: method.type,
    icon: method.icon,
    disableForNotAgeVerified: method.disableForNotAgeVerified,
  }));
};

const getDefaultShippingMethod = (
  shippingMethods: CheckoutV2Configuration["shippingMethods"] = []
): AnyShippingMethod => {
  if (!shippingMethods?.length) {
    return DEFAULT_AT_HOME_SHIPPING_METHOD;
  }

  const defaultShippingMethod = shippingMethods.find((method) => method.isDefault);

  return defaultShippingMethod || shippingMethods[0] || DEFAULT_AT_HOME_SHIPPING_METHOD;
};

const isShippingMethodEligible = (
  shippingMethod: AnyShippingMethod,
  { isShippingAgeVerificationEnabled, isAgeVerified }: ShippingMethodDefaultValueOptions
) => !isShippingAgeVerificationEnabled || isAgeVerified || !shippingMethod.disableForNotAgeVerified;

const getEligibleShippingMethod = (
  shippingMethods: CheckoutV2Configuration["shippingMethods"] = [],
  selectedMethodCode: string | undefined,
  options: ShippingMethodDefaultValueOptions
) => {
  const configuredShippingMethods = shippingMethods ?? [];
  const selectedShippingMethod = configuredShippingMethods.find((method) => method.methodCode === selectedMethodCode);

  if (selectedShippingMethod && isShippingMethodEligible(selectedShippingMethod, options)) {
    return selectedShippingMethod;
  }

  const firstEligibleShippingMethod = configuredShippingMethods.find((method) =>
    isShippingMethodEligible(method, options)
  );

  return firstEligibleShippingMethod || selectedShippingMethod || getDefaultShippingMethod(configuredShippingMethods);
};

export const shippingMethodFieldFactory: ShippingMethodFieldFactory = {
  getDerivedConfig: (shippingMethods = []) => {
    return {
      name: "shippingMethodCode",
      isHidden: !shippingMethods || shippingMethods.length === 0,
      options: generateFieldOptions(shippingMethods),
    };
  },
  getDefaultValue: (shippingMethods = [], options) => {
    const defaultShippingMethod = getDefaultShippingMethod(shippingMethods);

    return getEligibleShippingMethod(shippingMethods, defaultShippingMethod.methodCode, {
      isShippingAgeVerificationEnabled: options?.isShippingAgeVerificationEnabled ?? false,
      isAgeVerified: options?.isAgeVerified ?? false,
    }).methodCode;
  },
  getEligibleValue: (shippingMethods = [], selectedMethodCode, options) =>
    getEligibleShippingMethod(shippingMethods, selectedMethodCode, options).methodCode,
  getSelectedShippingMethodType: (methodCode, checkoutConfiguration) => {
    if (!methodCode) {
      const fallbackShippingMethod = getDefaultShippingMethod(checkoutConfiguration.shippingMethods);
      return fallbackShippingMethod?.type;
    }

    const selectedShippingMethod = checkoutConfiguration.shippingMethods.find(
      (method) => method.methodCode === methodCode
    );

    return selectedShippingMethod?.type;
  },
};
